import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import {
  ArrowLeft, Plus, Trash2, Zap, ZapOff, AlertTriangle,
  CircleCheck, CircleDashed, Loader2,
} from "lucide-react";


/**
 * Settings → Supplier Asset Libraries.
 *
 * Generic, supplier-agnostic registry management. Every registered
 * supplier appears as a data-driven row: no hard-coded Polytec/Laminex
 * markup. Adding a new supplier requires only a supplier name + a
 * provider_type keyed to a registered backend provider.
 */
export default function SupplierAssetLibrariesPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Add-supplier form state
  const [showAdd, setShowAdd] = useState(false);
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("zip_library");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, types] = await Promise.all([
        api.get("/supplier-registry"),
        api.get("/supplier-registry/provider-types"),
      ]);
      setSuppliers(rows.data);
      setProviderTypes(types.data.provider_types || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (row, updates) => {
    setBusyId(row.id);
    try {
      const r = await api.patch(`/supplier-registry/${row.id}`, updates);
      setSuppliers((prev) => prev.map((s) => (s.id === row.id ? r.data : s)));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusyId(null); }
  };

  const removeSupplier = async (row) => {
    if (!window.confirm(`Remove ${row.supplier_name} from the supplier registry? Uploaded libraries and matched assets are retained.`)) return;
    try {
      await api.delete(`/supplier-registry/${row.id}`);
      setSuppliers((prev) => prev.filter((s) => s.id !== row.id));
      toast.success(`${row.supplier_name} removed`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!formName.trim()) { toast.error("Supplier name is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/supplier-registry", {
        supplier_name: formName.trim(),
        provider_type: formType,
      });
      setSuppliers((prev) => [...prev, r.data]);
      setFormName(""); setFormType("zip_library"); setShowAdd(false);
      toast.success(`${r.data.supplier_name} registered`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-6xl mx-auto"
         data-testid="supplier-registry-page">
      <Link to="/settings" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Settings
      </Link>
      <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl">Supplier Asset Libraries</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Register the suppliers you want Subtend Studio to ingest digital assets from. Each supplier
            is served by a pluggable provider (ZIP library, manual upload, hosted feed or API).
            New suppliers default to review-only until you verify their matching quality.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="btn-primary text-xs"
          data-testid="btn-toggle-add-supplier">
          <Plus className="w-3 h-3" strokeWidth={1.5}/>
          Add supplier
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submitAdd} className="luxe-card p-6 mt-6"
              data-testid="add-supplier-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="text-xs">
              <div className="overline text-[10px]">Supplier</div>
              <input
                data-testid="input-new-supplier-name"
                className="input mt-1 w-full"
                placeholder="Caesarstone"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                autoFocus/>
            </label>
            <label className="text-xs">
              <div className="overline text-[10px]">Provider type</div>
              <select
                data-testid="select-new-provider-type"
                className="input mt-1 w-full"
                value={formType}
                onChange={(e) => setFormType(e.target.value)}>
                {providerTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
                <option value="custom_setup_required">custom (provider setup required)</option>
              </select>
            </label>
            <div className="flex items-end gap-2">
              <button type="submit" disabled={saving}
                      data-testid="btn-add-supplier-submit"
                      className="btn-primary text-xs">
                {saving ? "Saving…" : "Register"}
              </button>
              <button type="button" onClick={() => setShowAdd(false)}
                      className="btn-outline text-xs">Cancel</button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Choose <strong>zip_library</strong> for suppliers that provide an official
            downloadable asset library ZIP. Choose <strong>custom</strong> if you need a
            new provider adapter — the supplier will register in
            <em> Provider Setup Required</em> state until an adapter is deployed.
          </p>
        </form>
      )}

      <div className="mt-8" data-testid="supplier-registry-table">
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5}/> Loading…
          </div>
        ) : suppliers.length === 0 ? (
          <div className="luxe-card p-10 text-center text-sm text-muted-foreground"
               data-testid="supplier-registry-empty">
            No suppliers registered yet. Add your first supplier above.
          </div>
        ) : (
          <ul className="divide-y divide-border border border-border">
            <li className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <div className="col-span-3">Supplier</div>
              <div className="col-span-2">Provider</div>
              <div className="col-span-1 text-right">Assets</div>
              <div className="col-span-2">Last sync</div>
              <div className="col-span-2">Auto-populate</div>
              <div className="col-span-2 text-right">Status</div>
            </li>
            {suppliers.map((row) => (
              <SupplierRow
                key={row.id}
                row={row}
                busy={busyId === row.id}
                onPatch={(u) => patch(row, u)}
                onRemove={() => removeSupplier(row)}
              />
            ))}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground mt-6 flex items-center gap-2">
        <AlertTriangle className="w-3 h-3" strokeWidth={1.5}/>
        Supplier asset processing never changes pricing, project estimates or issued proposals.
        Manual thumbnails you set on a material are never overwritten.
      </p>
    </div>
  );
}


function SupplierRow({ row, busy, onPatch, onRemove }) {
  const [threshold, setThreshold] = useState(row.auto_populate_threshold ?? 95);
  useEffect(() => { setThreshold(row.auto_populate_threshold ?? 95); }, [row.auto_populate_threshold]);

  const statusColor = {
    active: "text-emerald-600",
    ready: "text-foreground",
    review_required: "text-amber-600",
    provider_setup_required: "text-amber-600",
    sync_failed: "text-red-600",
    disabled: "text-muted-foreground",
    importing: "text-blue-600",
    not_configured: "text-muted-foreground",
  }[row.status] || "text-foreground";

  const canAuto = row.provider_available && row.enabled;

  return (
    <li className="grid grid-cols-1 sm:grid-cols-12 gap-4 px-4 py-4 items-center"
        data-testid={`supplier-row-${row.supplier_name.toLowerCase()}`}>
      <div className="col-span-3">
        <div className="font-serif text-lg">{row.supplier_name}</div>
        <div className="text-[11px] text-muted-foreground">
          {row.supported_asset_types.join(" · ")}
        </div>
      </div>
      <div className="col-span-2 text-xs">
        <div>{row.provider_type}</div>
        <div className="text-[10px] text-muted-foreground">
          {row.provider_available ? "Provider available" : "Provider not deployed"}
        </div>
      </div>
      <div className="col-span-1 text-right text-xs" data-testid={`assets-${row.supplier_name.toLowerCase()}`}>
        {row.asset_count}
        <div className="text-[10px] text-muted-foreground">{row.library_count} libraries</div>
      </div>
      <div className="col-span-2 text-xs">
        {row.last_successful_sync_at
          ? new Date(row.last_successful_sync_at).toLocaleDateString("en-AU")
          : <span className="text-muted-foreground">Never</span>}
      </div>
      <div className="col-span-2 text-xs">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`toggle-auto-${row.supplier_name.toLowerCase()}`}
            disabled={busy || !canAuto}
            checked={row.auto_populate_enabled}
            onChange={(e) => onPatch({ auto_populate_enabled: e.target.checked })}
          />
          <span>{row.auto_populate_enabled ? `≥ ${threshold}%` : "Review-only"}</span>
        </label>
        {row.auto_populate_enabled && (
          <input
            type="number" min="60" max="100"
            data-testid={`threshold-${row.supplier_name.toLowerCase()}`}
            className="input mt-1 w-20 text-xs"
            value={threshold}
            onChange={(e) => setThreshold(parseInt(e.target.value || "95", 10))}
            onBlur={() => threshold !== row.auto_populate_threshold && onPatch({ auto_populate_threshold: threshold })}
          />
        )}
      </div>
      <div className="col-span-2 text-right text-xs flex items-center justify-end gap-3">
        <span className={`uppercase tracking-widest text-[10px] ${statusColor}`}
              data-testid={`status-${row.supplier_name.toLowerCase()}`}>
          {row.status.replace(/_/g, " ")}
        </span>
        {row.enabled ? (
          <button
            data-testid={`btn-disable-${row.supplier_name.toLowerCase()}`}
            disabled={busy}
            onClick={() => onPatch({ enabled: false })}
            className="text-muted-foreground hover:text-foreground"
            title="Disable">
            <ZapOff className="w-3 h-3" strokeWidth={1.5}/>
          </button>
        ) : (
          <button
            data-testid={`btn-enable-${row.supplier_name.toLowerCase()}`}
            disabled={busy}
            onClick={() => onPatch({ enabled: true })}
            className="text-muted-foreground hover:text-foreground"
            title="Enable">
            <Zap className="w-3 h-3" strokeWidth={1.5}/>
          </button>
        )}
        <button
          data-testid={`btn-remove-${row.supplier_name.toLowerCase()}`}
          disabled={busy}
          onClick={onRemove}
          className="text-muted-foreground hover:text-red-600"
          title="Remove">
          <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
        </button>
      </div>
    </li>
  );
}
